import React, { useEffect, useRef, useState } from "react";
import Icon9 from "../img/447e447e10282956b81289a96893ff9dcbf5c994c06cab37c72bc161cd26f0aa.svg";
import Icon19 from "../img/b4842688541f4c6389ef36bfe42945d814ca91f46ea5b94e336d43be5e0cabcf.svg";
import { useAuthFetch } from "../hooks/useAuthFetch";
import {
  ESTIMATING_ALL_ITEMS_URL,
  getImmediateEstimatingFolderUrl,
  resolveEstimatingFolderUrl,
  sanitizeEstimatingFolderUrl,
} from "../utils/estimatingFolderUrl";

const SHAREPOINT_BASE_URL = "https://westernfireproofing.sharepoint.com/sites/ProjectDatabase";

const PROJECT_DATABASE_FOLDERS = [
  "Billings",
  "Certified Payroll",
  "Change Orders",
  "CloseOut Documents",
  "Compliance",
  "Contracts",
  "Final Est  Proposal",
  "Insurance  Bonds",
  "Photos",
  "POs  Contracts",
  "Reports",
  "Subcontracts",
  "Submittals",
  "Safety",
];

const DISPLAY_NAME_OVERRIDES = {
  "POs  Contracts": "Purchase Orders",
  "CloseOut Documents": "Close-Out Documents",
  "Insurance  Bonds": "Insurance & Bonds",
  "Final Est  Proposal": "Final Est & Proposal",
};

const DOC_MANAGEMENT_ITEMS = [
  ...PROJECT_DATABASE_FOLDERS.slice(0, 6).map((name) => ({ type: "project-database", name })),
  {
    type: "estimating-folder",
    name: "Estimating Folders",
    displayName: "Estimating Folder",
  },
  ...PROJECT_DATABASE_FOLDERS.slice(6).map((name) => ({ type: "project-database", name })),
];

const buildProjectDatabaseUrl = (folderName, projectId) =>
  `${SHAREPOINT_BASE_URL}/${encodeURIComponent(folderName)}/Forms/AllItems.aspx?id=%2Fsites%2FProjectDatabase%2F${encodeURIComponent(folderName)}%2F${encodeURIComponent(projectId)}`;

const DocManagementLayout = ({ projectData }) => {
  const { authFetch, isReady } = useAuthFetch();
  const [folders, setFolders] = useState([]);
  const resolveAbortRef = useRef(null);
  const estimatingUrlRef = useRef(ESTIMATING_ALL_ITEMS_URL);

  // Instant URL from project data (no network) — click opens this immediately
  useEffect(() => {
    if (!projectData?.projectId) return;

    const estimatingUrl = getImmediateEstimatingFolderUrl({
      estimatingFolderLink: projectData.estimatingFolderLink,
      projectName: projectData.projectName,
    });
    estimatingUrlRef.current = estimatingUrl || ESTIMATING_ALL_ITEMS_URL;

    setFolders(
      DOC_MANAGEMENT_ITEMS.map((item) => {
        if (item.type === "estimating-folder") {
          return {
            id: `${item.name}-${projectData.projectId}`,
            name: item.name,
            displayName: item.displayName,
            type: item.type,
            sharepointUrl: estimatingUrlRef.current,
          };
        }

        return {
          id: `${item.name}-${projectData.projectId}`,
          name: item.name,
          displayName: DISPLAY_NAME_OVERRIDES[item.name] || item.name,
          type: item.type,
          sharepointUrl: buildProjectDatabaseUrl(item.name, projectData.projectId),
        };
      })
    );
  }, [projectData]);

  // Background refine by Project Number — does not block click
  useEffect(() => {
    if (!isReady || !projectData?.projectId) return;

    const projectNumber = projectData.projectId || projectData.projectNo;
    if (!projectNumber && !projectData.sharepointItemId && !projectData.projectName) {
      return undefined;
    }

    resolveAbortRef.current?.abort();
    const controller = new AbortController();
    resolveAbortRef.current = controller;

    (async () => {
      try {
        const resolvedUrl = await resolveEstimatingFolderUrl({
          authFetch,
          projectNumber,
          projectName: projectData.projectName,
          sharepointItemId: projectData.sharepointItemId,
          estimatingFolderLink: projectData.estimatingFolderLink,
          signal: controller.signal,
        });
        const safeUrl = sanitizeEstimatingFolderUrl(resolvedUrl);
        if (!safeUrl || controller.signal.aborted) return;

        estimatingUrlRef.current = safeUrl;
        setFolders((prev) =>
          prev.map((folder) =>
            folder.type === "estimating-folder"
              ? { ...folder, sharepointUrl: safeUrl }
              : folder
          )
        );
      } catch (error) {
        if (error?.name !== "AbortError") {
          // Keep immediate URL — click stays instant either way
        }
      }
    })();

    return () => controller.abort();
  }, [authFetch, isReady, projectData]);

  useEffect(() => {
    return () => {
      resolveAbortRef.current?.abort();
    };
  }, []);

  const handleNavClick = (folder) => {
    const url =
      folder.type === "estimating-folder"
        ? estimatingUrlRef.current || folder.sharepointUrl
        : folder.sharepointUrl;
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="flex flex-col px-7 pb-7 w-full rounded-none bg-stone-50">
      <div className="flex flex-col self-end mt-4 max-w-full text-base w-[259px]">
        {folders.map(({ id, displayName, sharepointUrl, type }, idx) => (
          <NavigationItem
            key={id}
            title={displayName}
            icon={Icon9}
            indicator={Icon19}
            className={idx > 0 ? "mt-4" : ""}
            onNavigate={() => handleNavClick({ type, sharepointUrl })}
          />
        ))}
      </div>
    </div>
  );
};

const NavigationItem = ({ title, icon, indicator, className, onNavigate }) => (
  <div
    className={`flex gap-4 items-center w-full max-w-[259px] ${className} cursor-pointer`}
    onClick={() => onNavigate?.()}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onNavigate?.();
      }
    }}
  >
    <img
      src={indicator}
      className="object-contain shrink-0 self-stretch my-auto aspect-[0.5] w-[5px]"
      alt="Indicator"
    />
    <div className="flex gap-3 items-center self-stretch my-auto">
      <img
        src={icon}
        className="object-contain shrink-0 self-stretch my-auto aspect-[1.31] w-[21px]"
        alt="Icon"
      />
      <div className="self-stretch my-auto w-[206px]" tabIndex="-1">
        {title}
      </div>
    </div>
  </div>
);

export default DocManagementLayout;
